import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Loader2, MapPin, Check } from 'lucide-react';

/**
 * Place autocomplete using /astrology/place-search.
 *
 * Props
 *   value          — current input text (controlled by parent)
 *   onTextChange   — called on every keystroke
 *   onSelect(loc)  — called when user picks from dropdown { place, lat, lon, tz }
 *   placeholder, className, testId
 *
 * NOTE: this component does NOT clear the parent's resolved location.
 * Callers should track text + resolved separately. If text differs from
 * resolved.place at submit time, callers can fall back to geocoding the text.
 */
export default function PlaceAutocomplete({
    value = '',
    onTextChange,
    onSelect,
    placeholder = 'Start typing — Mumbai, New York, …',
    className = '',
    testId = 'place-autocomplete',
}) {
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [justPicked, setJustPicked] = useState(false);
    const debounceRef = useRef(null);
    const wrapRef = useRef(null);
    // Track the most recent query so out-of-order responses don't flash a
    // stale "no matches" panel after a faster later request returned hits.
    const reqIdRef = useRef(0);

    // Debounced fetch driven by external value
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (justPicked) return;
        const text = (value || '').trim();
        if (text.length < 2) {
            setResults([]); setOpen(false); setLoading(false); return;
        }
        debounceRef.current = setTimeout(async () => {
            const myReqId = ++reqIdRef.current;
            setLoading(true);
            try {
                const { data } = await api.get('/astrology/place-search', { params: { q: text } });
                // Drop stale responses — only the latest fired query updates UI.
                if (myReqId !== reqIdRef.current) return;
                setResults(data.results || []);
                setOpen(true);
            } catch {
                if (myReqId !== reqIdRef.current) return;
                setResults([]);
            } finally {
                if (myReqId === reqIdRef.current) setLoading(false);
            }
        }, 250);   // tightened from 380ms — seed-city hits return in <50ms
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [value, justPicked]);

    // Close on outside click
    useEffect(() => {
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const choose = (r) => {
        setJustPicked(true);
        setOpen(false);
        onSelect?.(r);
        onTextChange?.(r.place);
        // Allow refetch on next manual edit
        setTimeout(() => setJustPicked(false), 100);
    };

    return (
        <div ref={wrapRef} className={`relative ${className}`} data-testid={testId}>
            <div className="relative">
                <Input
                    value={value || ''}
                    onChange={(e) => {
                        onTextChange?.(e.target.value);
                    }}
                    onFocus={() => results.length && setOpen(true)}
                    placeholder={placeholder}
                    className="bg-surface border-marigold/15 rounded-sm text-parchment h-11 pl-9 focus-visible:ring-1 focus-visible:ring-marigold"
                    data-testid={`${testId}-input`}
                />
                {loading
                    ? <Loader2 className="absolute left-2.5 top-3 text-marigold animate-spin" size={16} />
                    : <MapPin className="absolute left-2.5 top-3 text-slate-500" size={16} />}
            </div>

            {open && results.length > 0 && (
                <div
                    className="absolute z-50 mt-1 w-full bg-elevated border border-marigold/30 rounded-sm max-h-72 overflow-y-auto shadow-[0_12px_32px_rgba(0,0,0,0.6)]"
                    data-testid={`${testId}-dropdown`}
                >
                    {results.map((r, i) => (
                        <button
                            key={`${r.lat}-${r.lon}-${i}`}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); choose(r); }}
                            data-testid={`${testId}-option-${i}`}
                            className="w-full text-left px-4 py-3 hover:bg-marigold/10 text-sm text-parchment border-b border-marigold/5 last:border-b-0 transition-colors"
                        >
                            <div className="line-clamp-1 font-medium">{r.place}</div>
                            <div className="text-xs text-slate-500 mt-1">
                                {r.lat.toFixed(3)}, {r.lon.toFixed(3)} · {r.tz}
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {open && !loading && (value || '').trim().length >= 2 && results.length === 0 && (
                <div className="absolute z-50 mt-1 w-full bg-elevated border border-marigold/30 rounded-sm p-4 text-xs text-slate-500">
                    No matches — try a bigger nearby city.
                </div>
            )}
        </div>
    );
}
